import React from 'react';
import { useTranslation } from 'react-i18next';
import { sanitizeSystemEventSupportLink } from '../dto/system-events.dto.js';
import SystemEventReferenceValue from './system-event-reference-value.jsx';

function Field({ label, value, monospace = false }) {
  return (
    <div className="mb-2">
      <div className="text-muted small">{label}</div>
      <div className={monospace ? 'font-monospace small text-break' : 'text-break'}>{value || '-'}</div>
    </div>
  );
}

function SystemEventDetailDrawer({
  isOpen,
  event,
  isLoading,
  isSupportLoading,
  error,
  supportBundle,
  closeDetail,
  createSupportReference,
}) {
  const { t } = useTranslation();
  const supportLink = sanitizeSystemEventSupportLink(supportBundle?.supportLink);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className="offcanvas-backdrop fade show" onClick={closeDetail} />
      <aside
        className="offcanvas offcanvas-end show"
        style={{ visibility: 'visible', width: 'min(720px, 100vw)' }}
        aria-modal="true"
        role="dialog"
      >
        <div className="offcanvas-header border-bottom">
          <div>
            <h5 className="offcanvas-title">{t('systemEvents.detailTitle')}</h5>
            <div className="text-muted small">{event?.title || t('systemEvents.loadingDetail')}</div>
          </div>
          <button type="button" className="btn-close" aria-label={t('common.close')} onClick={closeDetail} />
        </div>
        <div className="offcanvas-body">
          {error ? <div className="alert alert-danger py-2">{error}</div> : null}
          {isLoading && !event ? <div className="text-muted">{t('common.loading')}</div> : null}
          {event ? (
            <>
              <div className="d-flex justify-content-between gap-3 align-items-start mb-3">
                <div>
                  <Field label={t('systemEvents.eventId')} value={event.id} monospace />
                  <div className="hstack gap-2 flex-wrap">
                    <span className={`badge ${event.severityBadgeClass}`}>{event.severityLabel}</span>
                    <span className="badge bg-secondary-subtle text-secondary">{event.sourceLabel}</span>
                    <span className="badge bg-primary-subtle text-primary">{event.categoryLabel}</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-primary text-nowrap"
                  onClick={createSupportReference}
                  disabled={isSupportLoading}
                >
                  <i className="ti ti-link me-1" />
                  {isSupportLoading ? t('systemEvents.copyingSupportReference') : t('systemEvents.createSupportReference')}
                </button>
              </div>

              {supportBundle?.supportReference ? (
                <div className="alert alert-info py-2">
                  <div className="fw-semibold">{t('systemEvents.supportReference')}</div>
                  <div className="font-monospace small text-break">{supportBundle.supportReference}</div>
                  {supportLink ? (
                    <div className="font-monospace small text-break">{supportLink}</div>
                  ) : null}
                </div>
              ) : null}

              <h6 className="mt-4 mb-3">{t('systemEvents.timeline')}</h6>
              <div className="row">
                <div className="col-md-6"><Field label={t('systemEvents.occurredAt')} value={event.occurredAtLabel} /></div>
                <div className="col-md-6"><Field label={t('systemEvents.recordedAt')} value={event.recordedAtLabel} /></div>
              </div>

              <h6 className="mt-4 mb-3">{t('systemEvents.classification')}</h6>
              <div className="row">
                <div className="col-md-4"><Field label={t('systemEvents.eventType')} value={event.eventTypeLabel} /></div>
                <div className="col-md-4"><Field label={t('systemEvents.retentionClass')} value={event.retentionLabel} /></div>
                <div className="col-md-4"><Field label={t('systemEvents.archiveStatus')} value={event.archiveLabel} /></div>
              </div>
              {event.archiveManifestHash && event.archiveManifestHash !== '-' ? (
                <Field label={t('systemEvents.archiveManifestHash')} value={event.archiveManifestHash} monospace />
              ) : null}

              <h6 className="mt-4 mb-3">{t('systemEvents.actorSubject')}</h6>
              <div className="row">
                <div className="col-md-6">
                  <Field
                    label={t('systemEvents.actor')}
                    value={<SystemEventReferenceValue reference={event.actor} label={event.actorLabel} />}
                  />
                </div>
                <div className="col-md-6">
                  <Field
                    label={t('systemEvents.subject')}
                    value={<SystemEventReferenceValue reference={event.subject} label={event.subjectLabel} />}
                  />
                </div>
              </div>

              <h6 className="mt-4 mb-3">{t('systemEvents.correlation')}</h6>
              <div className="row">
                <div className="col-md-6"><Field label={t('systemEvents.requestId')} value={event.requestId} monospace /></div>
                <div className="col-md-6"><Field label={t('systemEvents.correlationId')} value={event.correlationId} monospace /></div>
                <div className="col-md-6"><Field label={t('systemEvents.traceId')} value={event.traceId} monospace /></div>
                <div className="col-md-6"><Field label={t('systemEvents.sessionId')} value={event.sessionId} monospace /></div>
                <div className="col-md-6"><Field label={t('systemEvents.actionLogId')} value={event.actionLogId} monospace /></div>
                <div className="col-md-6"><Field label={t('systemEvents.jobRunId')} value={event.jobRunId} monospace /></div>
                <div className="col-md-12"><Field label={t('systemEvents.fingerprint')} value={event.fingerprint} monospace /></div>
              </div>

              <h6 className="mt-4 mb-3">{t('systemEvents.context')}</h6>
              <pre className="bg-light border rounded p-3 small text-break mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                {event.contextJson}
              </pre>
            </>
          ) : null}
        </div>
      </aside>
    </>
  );
}

export default SystemEventDetailDrawer;
